/**
 * Custom React Query hooks for cached data fetching
 * - Replaces direct service calls with cached queries
 * - Provides automatic refetching, caching, and deduplication
 * - Centralized query keys for easy invalidation
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getAllServices,
  getAllServicesAdmin,
  getAllPackages,
  getAllAddons,
  createService,
  updateService,
  deleteService,
  createPackage,
  updatePackage,
  deletePackage,
  createAddon,
  updateAddon,
  deleteAddon,
} from "@/services/package.service";
import {
  getReviews,
  getApprovedReviews,
  getReviewStats,
  updateReviewStatus,
  updateReviewFeatured,
  updateReviewHomepageDisplay,
  updateReviewOrder,
  deleteReview,
} from "@/services/review.service";
import { Service, Package, Addon, ReviewStatus } from "@/types";

/**
 * Query Keys
 * Centralized for consistency and easier cache invalidation
 */
export const queryKeys = {
  services: ["services"] as const,
  servicesAdmin: ["services", "admin"] as const,
  packages: ["packages"] as const,
  addons: ["addons"] as const,
  reviews: {
    all: ["reviews"] as const,
    approved: (limit?: number) => ["reviews", "approved", limit] as const,
    stats: ["reviews", "stats"] as const,
  },
};

/**
 * Services Queries
 */
export const useServices = () => {
  return useQuery({
    queryKey: queryKeys.services,
    queryFn: getAllServices,
    staleTime: 10 * 60 * 1000, // 10 minutes - services rarely change
  });
};

// Admin version - includes inactive services
export const useServicesAdmin = () => {
  return useQuery({
    queryKey: queryKeys.servicesAdmin,
    queryFn: getAllServicesAdmin,
    staleTime: 10 * 60 * 1000,
  });
};

export const useCreateService = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createService,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.services });
      queryClient.invalidateQueries({ queryKey: queryKeys.servicesAdmin });
    },
  });
};

export const useUpdateService = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Service> }) =>
      updateService(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.services });
      queryClient.invalidateQueries({ queryKey: queryKeys.servicesAdmin });
    },
  });
};

export const useDeleteService = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteService,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.services });
      queryClient.invalidateQueries({ queryKey: queryKeys.servicesAdmin });
      queryClient.invalidateQueries({ queryKey: queryKeys.packages });
      queryClient.invalidateQueries({ queryKey: queryKeys.addons });
    },
  });
};

/**
 * Packages Queries
 */
export const usePackages = () => {
  return useQuery({
    queryKey: queryKeys.packages,
    queryFn: getAllPackages,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useCreatePackage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createPackage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.packages });
    },
  });
};

export const useUpdatePackage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Package> }) =>
      updatePackage(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.packages });
    },
  });
};

export const useDeletePackage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deletePackage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.packages });
    },
  });
};

/**
 * Addons Queries
 */
export const useAddons = () => {
  return useQuery({
    queryKey: queryKeys.addons,
    queryFn: getAllAddons,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useCreateAddon = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createAddon,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.addons });
    },
  });
};

export const useUpdateAddon = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Addon> }) =>
      updateAddon(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.addons });
    },
  });
};

export const useDeleteAddon = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteAddon,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.addons });
    },
  });
};

/**
 * Reviews Queries
 */
export const useReviews = (filters?: {
  status?: ReviewStatus;
  customerId?: string;
  featured?: boolean;
  displayOnHomepage?: boolean;
}) => {
  return useQuery({
    queryKey: [...queryKeys.reviews.all, filters],
    queryFn: () => getReviews(filters),
    staleTime: 2 * 60 * 1000, // 2 minutes - reviews change more frequently
  });
};

export const useApprovedReviews = (limit?: number) => {
  return useQuery({
    queryKey: queryKeys.reviews.approved(limit),
    queryFn: () => getApprovedReviews(limit),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useReviewStats = () => {
  return useQuery({
    queryKey: queryKeys.reviews.stats,
    queryFn: getReviewStats,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useUpdateReviewStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      reviewId,
      status,
      userId,
      reason,
    }: {
      reviewId: string;
      status: ReviewStatus;
      userId: string;
      reason?: string;
    }) => updateReviewStatus(reviewId, status, userId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reviews.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.reviews.stats });
    },
  });
};

export const useUpdateReviewFeatured = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ reviewId, featured }: { reviewId: string; featured: boolean }) =>
      updateReviewFeatured(reviewId, featured),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reviews.all });
    },
  });
};

export const useUpdateReviewHomepageDisplay = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ reviewId, display }: { reviewId: string; display: boolean }) =>
      updateReviewHomepageDisplay(reviewId, display),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reviews.all });
    },
  });
};

export const useUpdateReviewOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ reviewId, order }: { reviewId: string; order: number }) =>
      updateReviewOrder(reviewId, order),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reviews.all });
    },
  });
};

export const useDeleteReview = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reviewId: string) => deleteReview(reviewId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reviews.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.reviews.stats });
      queryClient.invalidateQueries({
        queryKey: ["reviews", "approved"],
      });
    },
  });
};
